/**
 * Introducing Two-Foot Finishes Through Small-Sided Games.
 *
 * Coleman's copy (2026-10-18 rewrite), laid out as a journey: Explore, Add,
 * Test, then Keep Developing. The wording is his; the structure here only
 * decides which block each passage renders as.
 *
 * Three training intentions, not levels. The copy says so in the intro and
 * again at the end, and the layout never locks or gates anything.
 *
 *   01 Explore   see what they find; minimal coaching
 *   02 Add       expand the toolbox: strategies A/B/C, on-air optional
 *   03 Test      select and execute when winning matters:
 *                Require -> Reward -> Remove
 *   Keep developing   a cycle, not a checklist
 *
 * Each stage is an ordered list of `blocks` (see JourneyLayout for the kinds),
 * so the copy can change shape without the renderer changing.
 *
 * Games are referenced by drill title and resolve against the drills table at
 * read time. If a title is edited in the Content Manager, edit it here too or
 * the card silently disappears. Test's competitive games (Coleman, 2026-10-19)
 * aren't constrained to two feet themselves; the block says how to add it.
 * They have no intensity rating yet, so their tiles show no meter.
 *
 * Intensity is one to three, Coleman's ratings: the intensity of the
 * challenge, not a difficulty grade.
 *
 * No em dashes.
 */

export const TWO_FEET_MODULE_KEY = "two-feet-on-the-move";

const SPARRING   = "Hands on Hips Two-Foot Sparring";
const BUMP       = "Bump and Dribble into Two-Foot Finish";
const PUMP_FAKE  = "Two Foot Pump Fake Finishing";
const TAG_SPEED  = "Tag 2 Foot Speed Finishes";
const NO_STOP    = "Two Feet No Stop Finishes Opposite Side";
const TIMING_1V1 = "Two-Foot Timing 1v1 Finishing";

const JAIL_HELP  = "1v2 Jail Into Help";
const FINISH_1V2 = "1v2 Finishing Moderate";
const CONTACT    = "1v2 Advantage Contact";
const DUMPOFF    = "Dumpoff 2v2 (with gap option)";

const ON_AIR_TITLES = [
  "2 Foot Extension Reps",
  "3 Part 2 Foot Touch Progression",
  "2 Feet Coordination",
];

export const TWO_FEET_MODULE = {
  key: TWO_FEET_MODULE_KEY,
  title: "Introducing Two-Foot Finishes Through Small-Sided Games",
  sub: "Explore. Add. Test. A simple approach to developing two-foot finishing through small-sided games.",
  duration: "10 drills",
  // Its cover is a frame of its own video, like every masterclass (masterclassCovers.js copies it).
  coverVideoId: "HXXVMcP5pxlIJwATaS86z025h00gCPSRsRXB9UodNdYIg",

  discussion: {
    title: "Coach discussion",
    prompt: "Questions, observations, or experiences from running these games?",
  },

  chapters: [
    {
      // Kept from the first version so completion a coach already has carries over.
      id: "the-games",
      title: "Two-foot finishing journey",
      bare: true,
      blocks: [
        {
          kind: "journey",
          eyebrow: "Masterclass",
          title: "Introducing Two-Foot Finishes Through Small-Sided Games",
          tagline: "Explore. Add. Test.",
          meta: "10 games · about 10 min read",

          intro: [
            "Every coach understands the value of two-foot finishes. They're great options to help athletes maintain control, change direction, manipulate defenders, and finish through contact.",
            "But developing two-foot finishing is about much more than learning a jump stop or practicing a few finishing moves.",
            "We want players to develop a wide variety of solutions, recognize when they're useful, and execute them against live defense.",
            "This mini-masterclass introduces a simple approach to developing those abilities through small-sided games.",
          ],

          journeyLabel: "The Journey",
          note: "Keep in mind, these aren't three levels that athletes must complete one by one and linearly. They're three different training intentions that coaches can move between based on what their athletes need.",

          stages: [
            /* ── 01 EXPLORE ─────────────────────────────────────────────── */
            {
              id: "explore",
              name: "Explore",
              summary: "Discover what players can do and what they're willing to try.",
              headline: "Let them explore.",
              blocks: [
                { kind: "text", paras: [
                  "Before we start adding new solutions, let's see what athletes can discover on their own.",
                  "We like to use exploration-based games that invite athletes to experiment with different two-foot finishes early in the session.",
                  "Keep the coaching minimal. Manipulate the constraints a bit (e.g., must finish with the left, must jump right-left, must create contact first), encourage new solutions, and give athletes freedom to experiment.",
                ] },
                { kind: "split",
                  left:  { label: "For athletes", text: "This is an opportunity to discover and develop new options." },
                  right: { label: "For coaches",  text: "It's an opportunity to observe and learn." } },
                { kind: "text", paras: [
                  "Sometimes athletes will attempt solutions they're not very good at yet. Other times, certain solutions won't even seem to be on their radar. We're interested in both.",
                ] },
                { kind: "games", label: "Here are a few staples to get athletes exploring!", titles: [SPARRING, BUMP] },
                { kind: "qa",
                  question: "Should we require two-foot finishes here?",
                  answer: "Generally, yes.",
                  paras: [
                    "If we know we're trying to develop two-foot finishing, requiring athletes to finish off two feet is an efficient way to make sure they're consistently exploring the intended problem.",
                    "Within that constraint, however, we can leave plenty of freedom in how they solve it.",
                    "Occasionally, it can also be useful to allow both one-foot and two-foot finishes to see whether athletes naturally recognize and select opportunities to use two feet.",
                    "The constraint should depend on what you're trying to learn or develop.",
                  ] },
                { kind: "lookFor",
                  intro: "As athletes explore, pay attention to the solutions they attempt, the ones they gravitate toward, and the ones they seem to avoid.",
                  numbered: true,
                  groups: [
                    { title: "Footwork & Takeoffs", items: [
                      "Do they always come to a complete jump stop before finishing?",
                      "Can they take off two feet in stride?",
                      "Do they favor right-left or left-right plants?",
                    ] },
                    { title: "Movement Directions", items: [
                      "Can they jump in different directions, including horizontally or away from defenders?",
                      "Or do they primarily jump straight toward the basket?",
                    ] },
                    { title: "Finishing & Release Options", items: [
                      "What release points are they exploring?",
                      "Do they extend away from defenders, finish wide, or experiment with unusual positions?",
                      "Or do they mostly finish directly in front of the basket?",
                    ] },
                    { title: "Timing & Deception", items: [
                      "Do they experiment with pump fakes, pivots, or changes in timing?",
                      "Can they get defenders off the floor or out of position?",
                    ] },
                    { title: "Athletic & Acrobatic Solutions", items: [
                      "Do they try more explosive takeoffs, higher jumps, extended finishes, or other acrobatic solutions?",
                    ] },
                    { title: "Willingness to Experiment", items: [
                      "What are they willing to try, even if they struggle?",
                      "Which solutions do they rarely or never attempt?",
                    ] },
                  ],
                  note: "Remember that avoiding a solution doesn't necessarily mean an athlete is incapable of it. They may not recognize the opportunity, feel comfortable attempting it, or have even considered it." },
                { kind: "takeaway",
                  lead: "Notice what they try, what they repeat, and what they avoid.",
                  paras: ["Exploration helps athletes discover possibilities while giving coaches valuable information about where to go next."] },
              ],
            },

            /* ── 02 ADD ─────────────────────────────────────────────────── */
            {
              id: "add",
              name: "Add",
              summary: "Expand their toolbox with new strategies and movement options.",
              headline: "Expand their toolbox.",
              blocks: [
                { kind: "text", paras: [
                  "Now that we've explored what athletes can do, let's introduce some new possibilities.",
                  "Sometimes this means deliberately addressing a limitation we noticed during exploration.",
                  "Other times, especially with younger athletes or groups with a wide variety of abilities, it's simply about exposing them to more options.",
                  "Some athletes only need to experience a new solution once before they start experimenting with it. Others need repeated opportunities and different practice conditions before they can use it effectively.",
                  "The important thing here is that we're not just adding finishing *techniques*.",
                  "We're also adding **strategies**, and the movement options that help athletes execute those strategies.",
                ] },
                { kind: "strategyDiagram",
                  strategy: "Two-foot unpredictability",
                  options: ["Pump fake", "Pivot", "Delayed takeoff"],
                  caption: "For example, **two-foot unpredictability** is a strategy to score around the rim. A pump fake, pivot, or delayed takeoff might be one way of accomplishing it." },
                { kind: "strategies",
                  intro: "Here are a few different **strategies** we can explore.",
                  items: [
                    { id: "timing", letter: "A", name: "Timing & Deception",
                      headline: "Get defenders reacting.",
                      paras: [
                        "Two-foot finishing can provide opportunities to manipulate defenders through timing and deception.",
                        "Expose athletes to solutions that involve pump fakes, pivots, changes of pace, delayed takeoffs, and other ways of influencing a defender's positioning or timing.",
                        "The goal isn't simply to perform a particular fake, but more so to get defenders reacting and take advantage of the opportunities their reactions create.",
                      ],
                      focus: "Can athletes manipulate defenders and recognize the opportunities that follow?",
                      titles: [PUMP_FAKE, TIMING_1V1] },
                    { id: "speed", letter: "B", name: "Speed & Dynamic Jumps",
                      headline: "Two feet doesn't have to involve a full stop.",
                      paras: [
                        "A two-foot finish doesn't always require coming to a complete stop.",
                        "Athletes can take off in stride, change direction, and organize their feet in a variety of ways while moving at speed.",
                        "Expose players to two-foot solutions that involve faster approaches, different footwork patterns, and changes in movement direction.",
                        "These options are particularly useful when athletes need to cover ground, adjust their path, or organize their takeoff in a small window of time.",
                      ],
                      focus: "Can athletes find and execute different two-foot solutions while moving quickly or changing directions?",
                      titles: [TAG_SPEED] },
                    { id: "acrobatic", letter: "C", name: "Acrobatic Options",
                      headline: "Expand what's possible in the air.",
                      paras: [
                        "Some finishing situations require athletes to jump higher, extend farther, or organize their bodies in unfamiliar positions while mid-air.",
                        "Give them opportunities to explore these more explosive and acrobatic solutions, even at a young age!",
                        "This might mean emphasizing maximum vertical explosiveness, almost as if they're trying to dunk, or exploring different body positions and release points in the air.",
                        "The goal is to expand the range of movements and positions that athletes can comfortably access around the basket.",
                      ],
                      focus: "Can they organize and coordinate their jump for explosive takeoffs, then manipulate their body and ball in the air?",
                      titles: [NO_STOP] },
                  ] },
                { kind: "supplement",
                  label: "Optional",
                  title: "On-Air Coordination",
                  summary: "Sometimes it helps to isolate the movement patterns!",
                  body: [
                    "If an athlete struggles to coordinate certain movements, or keeps returning to the same few solutions, it can be useful to briefly simplify the environment.",
                    "Removing the defense can give athletes opportunities to experiment with unfamiliar footwork, takeoff patterns, and finishing positions.",
                    "This can be especially useful for players who struggle to organize their feet or access movements that are new to them.",
                    "Spend a few minutes exploring these movements, then bring them back into the game!",
                  ],
                  titlesLabel: "Examples",
                  titles: ON_AIR_TITLES },
              ],
            },

            /* ── 03 TEST ────────────────────────────────────────────────── */
            {
              id: "test",
              name: "Test",
              summary: "Challenge their ability to select and execute effective solutions when winning matters.",
              headline: "Can they make it work?",
              blocks: [
                { kind: "text", paras: [
                  "Exploration and targeted practice help athletes discover and develop new solutions.",
                  "But having more options doesn't automatically mean knowing when or how to use them when more degrees of freedom open up.",
                  "Now we want to challenge athletes to **select effective solutions when performance matters.**",
                  "Use more competitive, representative small-sided games where athletes must solve problems against defenders who are actively trying to win.",
                  "Rather than encouraging athletes to explore as many possibilities as possible, we're now challenging them to narrow their options based on what's actually effective in the situation.",
                  "This doesn't mean we stop experimenting altogether. It means the primary focus shifts toward performance.",
                ] },
                { kind: "progression",
                  title: "A Simple Progression: Require → Reward → Remove",
                  intro: "One way to gradually increase athletes' freedom is to change how strongly the activity encourages two-foot finishing.",
                  scale: ["More constrained", "More open"],
                  steps: [
                    { name: "Require", lead: "Two-foot finishes only.", paras: [
                      "Start with competitive games that require athletes to finish off two feet.",
                      "Players still need to find effective solutions against live defense, but we've constrained the task to ensure they're working on the skill we're trying to develop.",
                      "This is particularly useful when athletes aren't consistently finding two-foot opportunities without that constraint.",
                    ] },
                    { name: "Reward", lead: "Two-foot finishes are incentivized.", paras: [
                      "Now allow players to finish however they want, but provide an additional reward for successful two-foot finishes.",
                      "This opens up more freedom while encouraging athletes to recognize opportunities where two feet could be effective.",
                      "They're no longer required to use two feet, so their decisions become part of the challenge.",
                    ] },
                    { name: "Remove", lead: "No special incentive. Just basketball.", paras: [
                      "Finally, remove the specific requirement or reward.",
                      "Allow athletes to finish however they want and observe whether they recognize opportunities where a two-foot finish offers an advantage.",
                      "The goal isn't to make athletes finish off two feet every time.",
                      "It's to develop players who can recognize when a two-foot solution makes sense and execute it effectively.",
                    ] },
                  ],
                  note: { label: "Important", paras: [
                    "Require → Reward → Remove is a useful way to progressively open up the task, not a sequence every athlete must follow.",
                    "Coaches can enter at different points depending on the athlete's capabilities and the purpose of the session.",
                  ] } },
                { kind: "lookFor",
                  groups: [
                    { title: "Do the options actually show up?", items: [
                      "Are athletes using the solutions they explored earlier?",
                      "Or do they revert to the same familiar movements as soon as the environment becomes competitive?",
                    ] },
                    { title: "Can they create the opportunity?", items: [
                      "Maybe an athlete has the ability to execute a particular finish but struggles to create the angle or positioning needed to use it.",
                      "Can they manipulate the defender or adjust their path to create that opportunity?",
                    ] },
                    { title: "Can they execute under pressure?", items: [
                      "Do their footwork, timing, balance, or coordination break down as the game becomes faster or more competitive?",
                    ] },
                    { title: "Are they rushing?", items: [
                      "Are athletes trying to execute finishes too quickly rather than recognizing when they have opportunities to slow down, change timing, or manipulate the defender?",
                    ] },
                    { title: "Are they making effective choices?", items: [
                      "When given freedom, can they recognize when a two-foot finish is useful?",
                      "Are they selecting an effective solution based on the defender, available space, and the situation?",
                    ] },
                  ] },
                { kind: "takeaway",
                  lead: "Pay attention to why a solution isn't working, not just whether the athlete scores.",
                  points: [
                    "Sometimes the problem is a missing movement capability.",
                    "Sometimes it's an inability to create the necessary opportunity.",
                    "And sometimes the athlete has the option available but doesn't recognize when to use it.",
                  ],
                  paras: ["Those problems may require different training solutions."] },
                { kind: "games", label: "Competitive Games", titles: [JAIL_HELP, FINISH_1V2, CONTACT, DUMPOFF],
                  note: "**Any of these can be constrained to two feet: a basket only counts off a two-foot finish.** To reward it, make a two-foot make worth extra. To remove it, play them as they are. These activities should generally have fewer restrictions than the earlier exploration and development activities, with an emphasis on effective decisions and winning." },
              ],
            },
          ],

          close: {
            eyebrow: "Keep developing",
            title: "It's a cycle, not a checklist.",
            lead: "What you observe during testing should help inform what comes next.",
            maybes: [
              { text: "Maybe athletes need more opportunities to explore.", stage: "explore" },
              { text: "Maybe they're missing a particular strategy or movement option.", stage: "add" },
              { text: "Or maybe they already have plenty of solutions but need more experience recognizing and executing them under pressure.", stage: "test" },
            ],
            body: [
              "Move between Explore, Add, and Test based on what your athletes need.",
              "The ultimate goal isn't to develop players who can perform a long list of two-foot finishing moves.",
              "It's to develop players who recognize and execute effective two-foot solutions when the game calls for them.",
            ],
            loop: ["Explore", "Add", "Test", "Revisit"],
            cta: {
              label: "Build a training session",
              hint: "Pick the games that fit what you saw and build them into your next plan.",
              route: "plans",
            },
          },

          posterTime: {},

          intensity: {
            [SPARRING.toLowerCase()]: 1,
            [BUMP.toLowerCase()]: 1,
            [PUMP_FAKE.toLowerCase()]: 2,
            [TAG_SPEED.toLowerCase()]: 2,
            [NO_STOP.toLowerCase()]: 3,
            [TIMING_1V1.toLowerCase()]: 3,
          },
        },
      ],
    },
  ],
};
